import React from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Users, User, Package, Clock, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

export default function CoachingSection({ lessons, onBookNow }) {
  const getLevelColor = (level) => {
    const colors = {
      beginner: 'bg-green-100 text-green-700 border-green-200',
      intermediate: 'bg-blue-100 text-blue-700 border-blue-200',
      advanced: 'bg-purple-100 text-purple-700 border-purple-200',
      all_levels: 'bg-orange-100 text-orange-700 border-orange-200',
      all: 'bg-orange-100 text-orange-700 border-orange-200'
    };
    return colors[level] || 'bg-gray-100 text-gray-700';
  };

  const getTypeIcon = (type) => {
    const icons = {
      private: User,
      group: Users,
      package: Package
    };
    return icons[type] || Users;
  };

  return (
    <section className="py-24 bg-gradient-to-b from-white to-blue-50/30">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-block mb-4">
            <span className="px-4 py-2 rounded-full bg-blue-100 text-blue-600 text-sm font-semibold">
              Learn to Surf
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Professional Surf Coaching
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            From first-timers to seasoned riders, our experienced instructors will help you catch the perfect wave
          </p>
        </motion.div>

        {/* Coaching Image */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-16 rounded-3xl overflow-hidden shadow-2xl"
        >
          <img 
            src="/shope.webp"
            alt="Surf coaching on Essaouira beach"
            className="w-full h-[500px] object-cover object-top"
          />
        </motion.div>

        {/* Lessons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {lessons.map((lesson, index) => {
            // support both snake_case and camelCase shapes from admin/context
            const Icon = getTypeIcon(lesson.type || lesson.kind);
            const maxStudents = lesson.max_students ?? lesson.maxStudents ?? lesson.maxStudents;
            return (
              <motion.div
                key={lesson.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="p-6 h-full hover:shadow-xl transition-all duration-300 border-2 hover:border-blue-200 group cursor-pointer">
                  <div className="flex items-start justify-between mb-4">
                    <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 text-white group-hover:scale-110 transition-transform">
                      <Icon className="w-6 h-6" />
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getLevelColor(lesson.level)}`}>
                      {lesson.level.replace('_', ' ')}
                    </span>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                    {lesson.title}
                  </h3>

                  <p className="text-gray-600 mb-4 line-clamp-2">
                    {lesson.description}
                  </p>

                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
                    {lesson.duration && (
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{lesson.duration}</span>
                      </div>
                    )}
                    {maxStudents && (
                      <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        <span>Max {maxStudents}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div>
                      <span className="text-3xl font-bold text-gray-900">{lesson.price}</span>
                      <span className="text-gray-500 ml-1">MAD</span>
                    </div>
                    <Button
                      onClick={() => onBookNow(lesson)}
                      size="sm"
                      className="bg-blue-500 hover:bg-blue-600 rounded-full"
                    >
                      Book Now
                    </Button>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <Button
            onClick={onBookNow}
            size="lg"
            className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-8 py-6 text-lg rounded-full shadow-xl group"
          >
            View All Lessons
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
}